
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export const useNotificationReadStatus = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  const { data: readStatuses = [] } = useQuery({
    queryKey: ['notification-read-status'],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('notification_read_status')
        .select('*')
        .eq('user_id', user.id);
        
      if (error) {
        console.error('Error fetching read status:', error);
        return [];
      }
      
      return data || [];
    },
    enabled: !!user?.id,
  });

  const markAsRead = useMutation({
    mutationFn: async (notificationId: string) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { error } = await supabase
        .from('notification_read_status')
        .upsert({
          notification_id: notificationId,
          user_id: user.id,
          is_read: true,
          read_at: new Date().toISOString()
        }, {
          onConflict: 'notification_id,user_id'
        });
        
      if (error) throw error;
      return notificationId;
    },
    onSuccess: () => {
      // Invalidate both the read status and counts queries
      queryClient.invalidateQueries({ queryKey: ['notification-read-status'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
      // Force immediate refetch of counts
      queryClient.refetchQueries({ queryKey: ['notifications-count', 'unread'] });
    },
    onError: (error) => {
      toast.error("Fout bij het markeren als gelezen", {
        description: error.message
      });
    }
  });

  const markAsUnread = useMutation({
    mutationFn: async (notificationId: string) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      // In plaats van het record te verwijderen, updaten we de is_read status
      const { error } = await supabase
        .from('notification_read_status')
        .upsert({
          notification_id: notificationId,
          user_id: user.id,
          is_read: false,
          read_at: null
        }, {
          onConflict: 'notification_id,user_id'
        });
        
      if (error) throw error;
      return notificationId;
    },
    onSuccess: () => {
      // Invalidate both the read status and counts queries
      queryClient.invalidateQueries({ queryKey: ['notification-read-status'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
      // Force immediate refetch of counts
      queryClient.refetchQueries({ queryKey: ['notifications-count', 'unread'] });
    },
    onError: (error) => {
      toast.error("Fout bij het markeren als ongelezen", {
        description: error.message
      });
    }
  });

  const isNotificationRead = (notificationId: string): boolean => {
    return readStatuses.some(status => 
      status.notification_id === notificationId && 
      status.is_read
    );
  };

  return {
    markAsRead,
    markAsUnread,
    isNotificationRead
  };
};
